import { get, patch, post, type SessionUser } from './client'

/**
 * Périmètre PLATEFORME : l'annuaire des organisations.
 *
 * Module SÉPARÉ de `org.ts`, et pas une fonction de plus dedans : ces appels
 * traversent les tenants et n'existent que pour l'organisation de gestion. Les
 * mêler aux appels ordinaires ferait perdre cette distinction au premier coup
 * d'œil — côté serveur, la séparation est la même (`core/superuser/`).
 */

export interface OrganisationPlateforme {
  id: string
  name: string
  slug: string
  isHyperadmin: boolean
  createdAt: string
  memberCount: number
  /**
   * Compteurs MÉTIER enregistrés par les domaines côté serveur
   * (`registerOrgCounter`), indexés par leur clé — par exemple `documents`. Une
   * clé absente signifie que le domaine correspondant n'existe pas dans ce
   * déploiement : d'où le `Record` et le `?? 0` à l'affichage, jamais un
   * accès direct qui lèverait `undefined` en `NaN`. Sans domaine enregistré,
   * l'objet est simplement vide.
   */
  counters: Record<string, number>
}

export interface PageOrganisations {
  organisations: OrganisationPlateforme[]
  total: number
}

export function listOrganisations(params: {
  limit?: number
  offset?: number
  search?: string
} = {}): Promise<PageOrganisations> {
  const requete = new URLSearchParams()
  if (params.limit !== undefined) requete.set('limit', String(params.limit))
  if (params.offset !== undefined) requete.set('offset', String(params.offset))
  // Une recherche vide ne doit PAS partir : le serveur la refuserait (min 1
  // caractère après trim), et l'utilisateur verrait une erreur de validation
  // pour avoir simplement effacé son champ.
  const recherche = params.search?.trim() ?? ''
  if (recherche !== '') requete.set('search', recherche)

  const suffixe = requete.toString()
  return get<PageOrganisations>(`/superuser/organisations${suffixe === '' ? '' : `?${suffixe}`}`)
}

/**
 * Renomme une organisation depuis le périmètre plateforme.
 *
 * Retourne l'organisation sans ses compteurs : le serveur ne les recalcule pas
 * sur une mise à jour, et les inventer ici (zéro, ou l'ancienne valeur) ferait
 * afficher un chiffre faux comme s'il venait de la base.
 */
export function updateOrganisation(
  id: string,
  corps: { name: string },
): Promise<Omit<OrganisationPlateforme, 'memberCount' | 'counters'>> {
  return patch(`/superuser/organisations/${id}`, corps)
}

/**
 * « Jump » backoffice (E15-1) : entrer dans l'organisation d'un client depuis
 * l'annuaire, sans y avoir de compte. Le corps est VIDE : la cible est dans le
 * chemin, et l'origine est celle de la session côté serveur — jamais envoyée.
 *
 * Le droit est vérifié SERVEUR (organisation de gestion + rang owner/admin),
 * puis revalidé à chaque requête suivante. Le bouton ne fait que refléter cette
 * garde. La réponse est le profil de session EFFECTIF — celui de `/auth/me` —
 * à appliquer tel quel : organisation du client, `jump` renseigné.
 *
 * Retour : `quitterJump()` dans `auth.ts`, hors du périmètre plateforme.
 */
export function jumpOrganisation(id: string): Promise<SessionUser> {
  return post<SessionUser>(`/superuser/organisations/${id}/jump`)
}

// ---------------------------------------------------------------------------
// Catalogue des offres.
// ---------------------------------------------------------------------------

/** Les deux natures d'offre — miroir de `NATURES_PLAN` côté serveur. */
export type NaturePlan = 'subscription' | 'one_time'
/** Fréquences d'un abonnement ; `null` pour un ponctuel. */
export type IntervallePlan = 'month' | 'year'

export interface PlanPlateforme {
  id: string
  code: string
  kind: NaturePlan
  name: string
  description: string
  /** Centimes — jamais de flottant pour un prix (voir migration 0012). */
  amountCents: number
  currency: string
  billingInterval: IntervallePlan | null
  features: string[]
  isActive: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export interface PagePlans {
  plans: PlanPlateforme[]
  total: number
}

export interface NouveauPlan {
  code: string
  kind: NaturePlan
  name: string
  description?: string
  amountCents: number
  currency?: string
  billingInterval?: IntervallePlan | null
  features?: string[]
  sortOrder?: number
}

export function listPlans(
  params: { limit?: number; offset?: number; search?: string } = {},
): Promise<PagePlans> {
  const requete = new URLSearchParams()
  if (params.limit !== undefined) requete.set('limit', String(params.limit))
  if (params.offset !== undefined) requete.set('offset', String(params.offset))
  // Même règle que l'annuaire : une recherche blanche ne part pas (le serveur
  // la refuserait en validation).
  const recherche = params.search?.trim() ?? ''
  if (recherche !== '') requete.set('search', recherche)

  const suffixe = requete.toString()
  return get<PagePlans>(`/superuser/plans${suffixe === '' ? '' : `?${suffixe}`}`)
}

export function createPlan(corps: NouveauPlan): Promise<PlanPlateforme> {
  return post('/superuser/plans', corps)
}

/**
 * Modification partielle. `code` n'est PAS dans le type : l'identifiant d'un
 * plan est immuable (référé par les intégrations) — le serveur refuse la clé.
 */
export function updatePlan(
  id: string,
  corps: Partial<Omit<NouveauPlan, 'code'>> & { isActive?: boolean },
): Promise<PlanPlateforme> {
  return patch(`/superuser/plans/${id}`, corps)
}

// ---------------------------------------------------------------------------
// Connexions aux services externes (onglets de l'écran Services).
// ---------------------------------------------------------------------------

export interface HoteMail {
  host: string
  port: number
  secure: boolean
  /** Identifiant de compte — NON secret : seul le mot de passe est chiffré. */
  user: string
}

export interface ConnexionService {
  id: string
  code: string
  kind: string
  label: string
  /** Réglages non secrets (hôtes, ports, utilisateurs). */
  settings: { smtp?: HoteMail; imap?: HoteMail }
  /**
   * Drapeaux « un tel secret est enregistré » — clés de paquet → `true`.
   * La VALEUR ne quitte jamais le serveur : le mot de passe d'un relais SMTP
   * n'a rien à faire dans les outils de développement d'un navigateur.
   */
  secrets: Record<string, boolean>
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface PageConnections {
  connections: ConnexionService[]
  total: number
}

export function listConnections(
  kind: string,
  params: { limit?: number; offset?: number; search?: string } = {},
): Promise<PageConnections> {
  const requete = new URLSearchParams({ kind })
  if (params.limit !== undefined) requete.set('limit', String(params.limit))
  if (params.offset !== undefined) requete.set('offset', String(params.offset))
  // Même règle que partout : une recherche blanche ne part pas.
  const recherche = params.search?.trim() ?? ''
  if (recherche !== '') requete.set('search', recherche)
  return get<PageConnections>(`/superuser/services/connections?${requete.toString()}`)
}

export interface NouvelleBoiteMail {
  code: string
  label: string
  smtp: HoteMail
  imap?: HoteMail
  smtpPassword: string
  imapPassword?: string
}

export function createMailBox(corps: NouvelleBoiteMail): Promise<ConnexionService> {
  return post('/superuser/services/connections', { kind: 'mail', ...corps })
}

/**
 * Modification partielle d'une boîte mail. Triplet par champ : ABSENT du corps
 * = inchangé ; `null` = effacé (config IMAP, mot de passe) ; valeur = remplacé.
 */
export interface ModificationBoiteMail {
  label?: string
  smtp?: HoteMail
  imap?: HoteMail | null
  smtpPassword?: string | null
  imapPassword?: string | null
  isActive?: boolean
}

export function updateMailBox(
  id: string,
  corps: ModificationBoiteMail,
): Promise<ConnexionService> {
  return patch(`/superuser/services/connections/${id}`, corps)
}

// ---------------------------------------------------------------------------
// État du SERVICE (E12-1) — lecture seule.
//
// Deux lectures, aucune écriture : la configuration vient de l'environnement,
// validée au démarrage du serveur. Un réglage modifiable à chaud rendrait
// fausse la promesse « le service démarre complet ou pas du tout », et deux
// instances pourraient tourner avec deux configurations différentes sans que
// rien ne le dise.
//
// Ce que ces types NE portent PAS, et c'est délibéré : aucune valeur de
// configuration. Des drapeaux (`configure`) et des bornes publiques. Une URL,
// une clé ou l'adresse d'astreinte ne traversent pas cette frontière — elles
// finiraient dans les outils de développement du navigateur, puis dans la
// capture d'écran d'un ticket de support.
// ---------------------------------------------------------------------------

export interface ConfigurationService {
  environnement: 'development' | 'test' | 'production'
  version: string
  courriel: { transport: 'dev' | 'smtp'; relaisConfigure: boolean; maxTentatives: number }
  serviceDocumentaire: { configure: boolean; cleInterneConfiguree: boolean }
  alerte: { destinataireConfigure: boolean; seuilEchecs: number; fenetreMinutes: number }
  bornes: {
    signatureDocumentMaxOctets: number
    horodatageDocumentMaxOctets: number
    expirationDefautJours: number
    expirationMaxJours: number
    webhooksMaxTentatives: number
  }
  sondes: { cle: string; libelle: string }[]
}

export interface IncidentService {
  id: string
  cle: string
  ouvertLe: string
  derniereOccurrenceLe: string
  occurrences: number
  /** `null` = personne n'a été prévenu (destinataire non configuré). */
  alerteEnvoyeeLe: string | null
  detail: string | null
}

export function getConfigurationService(): Promise<ConfigurationService> {
  return get('/admin/service/configuration')
}

export function getIncidentsService(): Promise<{ incidents: IncidentService[] }> {
  return get('/admin/service/incidents')
}

/**
 * La SANTÉ du service (E12-5) : files, reprises, conciliation, sceau d'audit.
 *
 * Une seule lecture plutôt que quatre : l'exploitant se demande « est-ce que ça
 * va », et quatre appels donneraient quatre instants différents — un écran
 * composé d'instantanés décalés raconte une histoire que personne n'a vécue.
 */
export interface RepriseParKind {
  kind: string
  /** Travaux terminés sur la fenêtre, toutes issues confondues. */
  termines: number
  /** Ceux qui ont demandé plus d'une tentative. */
  repris: number
  echoues: number
  /** Tentatives cumulées des seuls travaux repris. */
  tentativesCumulees: number
}

export interface FileParKind {
  kind: string
  enAttente: number
  enCours: number
  /** Âge du plus ancien travail encore en file. Un compte seul ne dit pas si
   *  la file est une pointe de charge ou un consommateur mort. */
  plusVieuxMinutes: number | null
}

export interface SanteService {
  fenetreHeures: number
  reprises: RepriseParKind[]
  files: FileParKind[]
  incidentsOuverts: number
  ecartsConciliationOuverts: number
  /** `null` quand la chaîne n'a jamais été scellée. Aucune empreinte : elle
   *  n'aide à rien qu'on puisse faire depuis un navigateur. */
  audit: { scelleLe: string; entreesCouvertes: number; sceauPublie: boolean } | null
  sain: boolean
}

export function getSanteService(): Promise<SanteService> {
  return get('/admin/service/sante')
}
